import React, { useState, useRef, useEffect } from 'react';

/**
 * TwoFactorModal — Reusable 2FA verification prompt.
 *
 * Shows when a sensitive admin action requires 2FA verification.
 * On success, calls onVerify(code) which should retry the original request
 * with the X-2FA-Code header.
 */
const TwoFactorModal = ({ isOpen, onClose, onVerify, error }) => {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setSubmitting(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) return;
    setSubmitting(true);
    try {
      await onVerify(code);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#6C5CE7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <h3 style={styles.title}>2FA Verification Required</h3>
          <p style={styles.subtitle}>
            This action requires two-factor authentication. Enter the 6-digit code from your authenticator app.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            pattern="\d{6}"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            style={styles.input}
            disabled={submitting}
          />

          {error && (
            <p style={styles.error}>{error}</p>
          )}

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.btnCancel} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.btnVerify} disabled={submitting || code.length !== 6}>
              {submitting ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(2px)',
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    padding: '32px',
    width: '100%',
    maxWidth: '380px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    margin: '16px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#212529',
    margin: '12px 0 8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6C757D',
    margin: 0,
    lineHeight: 1.5,
  },
  input: {
    width: '100%',
    padding: '14px',
    fontSize: '24px',
    fontFamily: 'monospace',
    fontWeight: 700,
    textAlign: 'center',
    letterSpacing: '8px',
    border: '2px solid #DEE2E6',
    borderRadius: '8px',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  error: {
    color: '#DC3545',
    fontSize: '13px',
    textAlign: 'center',
    margin: '10px 0 0',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
  },
  btnCancel: {
    flex: 1,
    padding: '10px',
    border: '1px solid #DEE2E6',
    background: '#fff',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    color: '#495057',
  },
  btnVerify: {
    flex: 1,
    padding: '10px',
    border: 'none',
    background: '#6C5CE7',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default TwoFactorModal;
